/* eslint-disable react/prop-types */
/* eslint-disable no-shadow */
import React from 'react';
import Loadable from 'react-loadable';
import { Route, Redirect, Switch } from 'react-router-dom';

import AuthorizedRoute from '@components/AuthorizedRoute/AuthorizedRoute';
import EditableRoute from '@components/EditableRoute';

import EventsRoutes from '@modules/events/routes';
import LitigationRoutes from '@modules/litigation/routes';
import MobileRoutes from '@modules/mobileManager/routes';
import AnalysisRoutes from '@modules/analysis/routes';
import DataCaptureRoutes from '@modules/dataCapture/routes';
import AuditsRoutes from '@modules/audits/routes';
import AppraisalRoutes from '@modules/appraisal/routes';
import MraRoutes from '@modules/mra/routes';
import AppraisalAdminRoutes from '@modules/appraisalAdmin/routes';
import ProcessManagerRoutes from '@modules/processManager/routes';
import BiRoutes from '@modules/bi/routes';
import AppealsRoutes from '@modules/appeals/routes';
import QueueRoutes from '@modules/queue/routes';
import VirtualHelpRoutes from '@modules/virtualHelp/routes';
import ConferencingRoutes from '@modules/conferencing/routes';

import App from './pages/app/';
import AuthorizedWrapper from './pages/authorizedWrapper';
import PortalNotificationsPage from '@modules/appraisalAdmin/pages/ElectronicDeliveryRequestsPortalNotifications';
import PortalNotificationRecords from '@modules/appraisalAdmin/pages/ElectronicDeliveryRequestsPortalNotifications/records';

const Loading = () => <div />;

const Home = Loadable({
  loader: () => import('./pages/home'),
  loading: Loading,
});
const Login = Loadable({
  loader: () => import('./pages/login'),
  loading: Loading,
});
const Logout = Loadable({
  loader: () => import('./pages/logout'),
  loading: Loading,
});
const ResetPassword = Loadable({
  loader: () => import('./pages/resetPassword'),
  loading: Loading,
});
const EquityFinderMain = Loadable({
  loader: () => import('./pages/equityFinder/main'),
  loading: Loading,
});
const DashboardMain = Loadable({
  loader: () => import('./pages/equityFinder/dashboard'),
  loading: Loading,
});
const AppraiserDashboard = Loadable({
  loader: () => import('./pages/equityFinder/dashboard/appraiser'),
  loading: Loading,
});
const ManagerDashboard = Loadable({
  loader: () => import('./pages/equityFinder/dashboard/manager'),
  loading: Loading,
});
const PropertyList = Loadable({
  loader: () => import('./pages/equityFinder/propertyList'),
  loading: Loading,
});
const MasterList = Loadable({
  loader: () => import('./pages/equityFinder/masterList'),
  loading: Loading,
});
const VersionList = Loadable({
  loader: () => import('./pages/equityFinder/versionList'),
  loading: Loading,
});
const RegressionAnalysis = Loadable({
  loader: () => import('./pages/equityFinder/regressionAnalysis'),
  loading: Loading,
});
const Presentation = Loadable({
  loader: () => import('./pages/equityFinder/presentation'),
  loading: Loading,
});
const GlobalLibrary = Loadable({
  loader: () => import('./pages/equityFinder/globalLibrary'),
  loading: Loading,
});

const Configurations = Loadable({
  loader: () => import('./pages/equityFinder/configurations'),
  loading: Loading,
});
const ConfigurationsManage = Loadable({
  loader: () => import('./pages/equityFinder/configurations/manage'),
  loading: Loading,
});
const RuleSetManage = Loadable({
  loader: () => import('./pages/equityFinder/configurations/ruleSetManage'),
  loading: Loading,
});
const RuleManage = Loadable({
  loader: () => import('./pages/equityFinder/configurations/ruleManage'),
  loading: Loading,
});
const ConfigurationsCompManage = Loadable({
  loader: () =>
    import('./pages/equityFinder/configurations/compSelectionManage'),
  loading: Loading,
});

const CompGridConfigurations = Loadable({
  loader: () => import('./pages/equityFinder/compGridConfiguration'),
  loading: Loading,
});
const CompGridManage = Loadable({
  loader: () =>
    import('./pages/equityFinder/compGridConfiguration/compGridManage'),
  loading: Loading,
});

const SystemConfigurations = Loadable({
  loader: () => import('./pages/equityFinder/systemConfiguration'),
  loading: Loading,
});
const SystemManage = Loadable({
  loader: () => import('./pages/equityFinder/systemConfiguration/systemManage'),
  loading: Loading,
});

const BaseUnitConfigurations = Loadable({
  loader: () => import('./pages/equityFinder/baseUnitConfiguration'),
  loading: Loading,
});
const TemplateConfigurations = Loadable({
  loader: () => import('./pages/equityFinder/templateConfiguration'),
  loading: Loading,
});

const ProdigyScoreConfiguration = Loadable({
  loader: () => import('./pages/equityFinder/prodigyScoreConfiguration'),
  loading: Loading,
});
const ScoreManage = Loadable({
  loader: () =>
    import('./pages/equityFinder/prodigyScoreConfiguration/scoreManage'),
  loading: Loading,
});

const GlobalConfigurations = Loadable({
  loader: () => import('./pages/equityFinder/globalScoring'),
  loading: Loading,
});
const GlobalConfigurationsCompManage = Loadable({
  loader: () =>
    import('./pages/equityFinder/globalScoring/compSelectionManage'),
  loading: Loading,
});
const Property = Loadable({
  loader: () => import('./pages/equityFinder/property'),
  loading: Loading,
});

const Dataload = Loadable({
  loader: () => import('./pages/equityFinder/dataload'),
  loading: Loading,
});
const Datalog = Loadable({
  loader: () => import('./pages/equityFinder/datalog'),
  loading: Loading,
});

const MaintenanceMain = Loadable({
  loader: () => import('./pages/maintenance/main'),
  loading: Loading,
});
const Modules = Loadable({
  loader: () => import('./pages/maintenance/modules'),
  loading: Loading,
});
const Roles = Loadable({
  loader: () => import('./pages/maintenance/roles'),
  loading: Loading,
});
const UserRoles = Loadable({
  loader: () => import('./pages/maintenance/userRoles'),
  loading: Loading,
});
const Users = Loadable({
  loader: () => import('./pages/maintenance/users'),
  loading: Loading,
});
const UserStatus = Loadable({
  loader: () => import('./pages/maintenance/userStatus'),
  loading: Loading,
});
const TaxTransparencyAdmin = Loadable({
  loader: () => import('./pages/maintenance/taxTransparency'),
  loading: Loading,
});

const PublicPortalAdmin = Loadable({
  loader: () => import('./pages/maintenance/portalPublic'),
  loading: Loading,
});

const PublicWebsiteAdmin = Loadable({
  loader: () => import('./pages/maintenance/websiteAdmin'),
  loading: Loading,
});

const MaintenanceAdmin = Loadable({
  loader: () => import('./pages/maintenance/maintenanceAdmin'),
  loading: Loading,
});

const AppraisalDemoMain = Loadable({
  loader: () => import('./pages/appraisalDemo/main'),
  loading: Loading,
});
const AppraisalDemoPropertySearch = Loadable({
  loader: () => import('./pages/appraisalDemo/propertySearch'),
  loading: Loading,
});
const AppraisalDemoSummary = Loadable({
  loader: () => import('./pages/appraisalDemo/summary'),
  loading: Loading,
});
const AppraisalDemoZillowTable = Loadable({
  loader: () => import('./pages/appraisalDemo/summary/zillowTable'),
  loading: Loading,
});
const AppraisalDemoMarketArea = Loadable({
  loader: () => import('./pages/appraisalDemo/marketArea'),
  loading: Loading,
});
const AppraisalDemoDashboard = Loadable({
  loader: () => import('./pages/appraisalDemo/dashboard'),
  loading: Loading,
});

const CIPageWrapper = Loadable({
  loader: () => import('./modules/ci/components/PageWrapper'),
  loading: Loading,
});
const CIDashboard = Loadable({
  loader: () => import('./modules/ci/pages/dashboard'),
  loading: Loading,
});
const CIDashboardSearch = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/search'),
  loading: Loading,
});
const GISSettings = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/gisSettings'),
  loading: Loading,
});
const SymbologySettings = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/symbologySettings'),
  loading: Loading,
});
const CISettings = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/settings'),
  loading: Loading,
});
const CISearchRecord = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/searchRecord'),
  loading: Loading,
});
const CINewRecord = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/createNew'),
  loading: Loading,
});
const CISearchImagery = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/imagery'),
  loading: Loading,
});
const CIAppraisalImagery = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/appraisalImagery'),
  loading: Loading,
});
const CICountyMap = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/countyMap'),
  loading: Loading,
});
const CISearchSketch = Loadable({
  loader: () => import('./modules/ci/pages/ciDashboard/editSketch'),
  loading: Loading,
});

const SketchPageWrapper = Loadable({
  loader: () => import('./modules/sketch/components/PageWrapper'),
  loading: Loading,
});
const SketchDashboard = Loadable({
  loader: () => import('./modules/sketch/pages/dashboard'),
  loading: Loading,
});
const SketchDashboardSearch = Loadable({
  loader: () => import('./modules/sketch/pages/sketchDashboard/search'),
  loading: Loading,
});
const SketchSearchDetail = Loadable({
  loader: () => import('./modules/sketch/pages/sketchDashboard/searchDetail'),
  loading: Loading,
});
const SketchCreateNew = Loadable({
  loader: () => import('./modules/sketch/pages/sketchDashboard/createNew'),
  loading: Loading,
});
const Sketch = Loadable({
  loader: () => import('./modules/sketch/pages/create/SketchWrapper'),
  loading: Loading,
});

const AssessmentPageWrapper = Loadable({
  loader: () => import('./modules/assessment/components/PageWrapper'),
  loading: Loading,
});
const AssessmentExemption = Loadable({
  loader: () => import('./modules/assessment/pages/exemption'),
  loading: Loading,
});
const AssessmentTaxingUnitMaintenance = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/maintenance'),
  loading: Loading,
});
const AssessmentExemptionListing = Loadable({
  loader: () => import('./modules/bi/pages/taxingUnitOptions'),
  loading: Loading,
});
const AssessmentTaxingUnitGeneral = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/general'),
  loading: Loading,
});
const AssessmentTaxingUnitLibrary = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/library'),
  loading: Loading,
});
const AssessmentTaxingUnitVersion = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/version'),
  loading: Loading,
});
const AssessmentTaxingUnitWorksheet = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/worksheet'),
  loading: Loading,
});
const AssessmentTaxingUnitHearingNotice = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/hearing'),
  loading: Loading,
});
const AssessmentTaxingUnitEvent = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/event'),
  loading: Loading,
});
const AssessmentTaxingUnitRevisionHistory = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnit/revisionHistory'),
  loading: Loading,
});
const AssessmentTaxingUnitGroups = Loadable({
  loader: () => import('./modules/assessment/pages/taxingUnitGroups'),
  loading: Loading,
});
const AssessmentTaxingUnitGroupsCreate = Loadable({
  loader: () =>
    import('./modules/assessment/pages/taxingUnitGroups/subpages/create'),
  loading: Loading,
});
const AssessmentTaxingUnitGroupsGroup = Loadable({
  loader: () =>
    import('./modules/assessment/pages/taxingUnitGroups/subpages/group'),
  loading: Loading,
});

const ElectronicCorrespondenceConfiguration = Loadable({
  loader: () => import('./pages/maintenance/electronicCorrespondence'),
  loading: Loading,
});

const RevisionHistorySearch = Loadable({
  loader: () => import('./pages/maintenance/revisionHistorySearch'),
  loading: Loading,
});

const DailyJobsAdmin = Loadable({
  loader: () => import('./pages/maintenance/dailyJobsAdmin'),
  loading: Loading,
});

export const getRoutes = () => (
  <Route
    path="/"
    render={(props) => (
      <App {...props}>
        <Switch>
          <Route component={Login} path="/login" />
          <Route component={Logout} path="/logout" />
          <Route component={ResetPassword} path="/reset" />
          <Route
            path="/"
            render={(props) => (
              <AuthorizedWrapper {...props}>
                <Switch>
                  <AuthorizedRoute
                    exact
                    path="/"
                    render={(props) => <Home {...props} />}
                  />
                  <AuthorizedRoute
                    path="/equityFinder"
                    render={(props) => (
                      <EquityFinderMain {...props}>
                        <Switch>
                          <EditableRoute
                            component={PropertyList}
                            path="/equityFinder/propertyList"
                          />
                          <EditableRoute
                            component={Property}
                            path="/equityFinder/subject/:propertyId/:year"
                          />
                          <EditableRoute
                            component={Property}
                            path="/equityFinder/subject/:propertyId/:year/:version/:type"
                          />
                          <EditableRoute
                            component={RegressionAnalysis}
                            path="/equityFinder/regressionAnalysis/:propertyId/:year"
                          />
                          <EditableRoute
                            component={MasterList}
                            path="/equityFinder/masterList/:propertyId/:year"
                          />
                          <EditableRoute
                            component={VersionList}
                            path="/equityFinder/versionList/:propertyId/:year"
                          />
                          <EditableRoute
                            component={Presentation}
                            path="/equityFinder/presentation/:propertyId/:year"
                          />
                          <EditableRoute
                            component={GlobalLibrary}
                            path="/equityFinder/globalLibrary"
                          />
                          <EditableRoute
                            component={Configurations}
                            exact
                            path="/equityFinder/configurations/rule"
                          />
                          <EditableRoute
                            component={ConfigurationsManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/create/:categoryTitle"
                          />
                          <EditableRoute
                            component={ConfigurationsManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/edit/:categoryId/:type"
                          />
                          <EditableRoute
                            component={RuleSetManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/edit/:categoryId/regression/:ruleSetId"
                          />
                          <EditableRoute
                            component={RuleManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/edit/:categoryId/regression/:ruleSetId/rule/create/:field"
                          />
                          <EditableRoute
                            component={RuleManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/edit/:categoryId/regression/:ruleSetId/rule/edit/:ruleId"
                          />
                          <EditableRoute
                            component={ConfigurationsCompManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/edit/:categoryId/comp/:ruleId"
                          />
                          <EditableRoute
                            component={ConfigurationsCompManage}
                            exact
                            path="/equityFinder/configurations/rule/manage/edit/:categoryId/comp/:ruleId/:field"
                          />
                          <EditableRoute
                            component={CompGridConfigurations}
                            exact
                            path="/equityFinder/configurations/compgrid"
                          />
                          <EditableRoute
                            component={CompGridManage}
                            exact
                            path="/equityFinder/configurations/compgrid/:configurationId"
                          />
                          <EditableRoute
                            component={SystemConfigurations}
                            exact
                            path="/equityFinder/configurations/system"
                          />
                          <EditableRoute
                            component={SystemManage}
                            exact
                            path="/equityFinder/configurations/system/:configurationId"
                          />
                          <EditableRoute
                            component={BaseUnitConfigurations}
                            exact
                            path="/equityFinder/configurations/base"
                          />
                          <EditableRoute
                            component={TemplateConfigurations}
                            exact
                            path="/equityFinder/configurations/template"
                          />
                          <EditableRoute
                            component={ProdigyScoreConfiguration}
                            exact
                            path="/equityFinder/configurations/score"
                          />
                          <EditableRoute
                            component={ScoreManage}
                            exact
                            path="/equityFinder/configurations/score/:configurationId"
                          />
                          <EditableRoute
                            component={GlobalConfigurations}
                            exact
                            path="/equityFinder/globalconfigurations"
                          />
                          <EditableRoute
                            component={GlobalConfigurationsCompManage}
                            exact
                            path="/equityFinder/globalconfigurations/edit/:ruleId"
                          />
                          <EditableRoute
                            component={Dataload}
                            path="/equityFinder/dataload"
                          />
                          <EditableRoute
                            component={Datalog}
                            path="/equityFinder/datalog"
                          />
                          <Route
                            path="/equityFinder/dashboard"
                            render={(props) => (
                              <DashboardMain {...props}>
                                <Switch>
                                  <EditableRoute
                                    component={ManagerDashboard}
                                    exact
                                    path="/equityFinder/dashboard/manager"
                                  />
                                  <EditableRoute
                                    component={AppraiserDashboard}
                                    exact
                                    path="/equityFinder/dashboard/appraiser"
                                  />
                                  <EditableRoute
                                    component={AppraiserDashboard}
                                    exact
                                    path="/equityFinder/dashboard/supervisor"
                                  />
                                  <Redirect to="/equityFinder/dashboard/appraiser" />
                                </Switch>
                              </DashboardMain>
                            )}
                          />
                          <Redirect to="/equityFinder/dashboard" />
                        </Switch>
                      </EquityFinderMain>
                    )}
                  />
                  <AuthorizedRoute
                    path="/apprdemo"
                    render={(props) => (
                      <AppraisalDemoMain {...props}>
                        <Switch>
                          <Route
                            component={AppraisalDemoPropertySearch}
                            exact
                            path="/apprdemo/search"
                          />
                          <Route
                            component={AppraisalDemoSummary}
                            exact
                            path="/apprdemo/summary/:propertyId/:year"
                          />
                          <Route
                            component={AppraisalDemoZillowTable}
                            exact
                            path="/apprdemo/summary-zillow/:propertyId/:year"
                          />
                          <Route
                            component={AppraisalDemoSummary}
                            exact
                            path="/apprdemo/summary-edit/:propertyId/:year"
                          />
                          <Route
                            component={AppraisalDemoMarketArea}
                            exact
                            path="/apprdemo/market"
                          />
                          <Route
                            component={AppraisalDemoDashboard}
                            exact
                            path="/apprdemo/dashboard"
                          />
                          <Route
                            component={AppraisalDemoDashboard}
                            exact
                            path="/apprdemo/dashboard-edit"
                          />
                          <Redirect to="/apprdemo/search" />
                        </Switch>
                      </AppraisalDemoMain>
                    )}
                  />

                  <AuthorizedRoute path="/appraisal" render={AppraisalRoutes} />
                  <AuthorizedRoute
                    path="/appraisal-admin"
                    render={AppraisalAdminRoutes}
                  />
                  <AuthorizedRoute
                    path="/appraisal-process-manager"
                    render={ProcessManagerRoutes}
                  />
                  <AuthorizedRoute path="/bi" render={BiRoutes} />
                  <AuthorizedRoute
                    path="/prodigyVirtualHelp"
                    render={VirtualHelpRoutes}
                  />
                  <AuthorizedRoute
                    path="/conferencing"
                    render={ConferencingRoutes}
                  />

                  <AuthorizedRoute
                    path="/maintenance"
                    render={(props) => (
                      <MaintenanceMain {...props}>
                        <Switch>
                          <Route
                            path="/maintenance/modules/:moduleId?"
                            exact
                            render={(props) => (
                              <Modules {...props}>
                                <Switch>
                                  <Route
                                    component={Roles}
                                    exact
                                    path="/maintenance/modules/:moduleId"
                                  />
                                </Switch>
                              </Modules>
                            )}
                          />
                          <Route
                            path="/maintenance/users/:userType"
                            exact
                            component={Users}
                          />
                          <Route
                            component={UserRoles}
                            exact
                            path="/maintenance/users/:userType/:userId"
                          />
                          <Route
                            path="/maintenance/status-users"
                            exact
                            component={UserStatus}
                          />
                          <Route
                            component={TaxTransparencyAdmin}
                            exact
                            path="/maintenance/tax-transparency"
                          />
                          <Route
                            component={PublicPortalAdmin}
                            exact
                            path="/maintenance/public-portal"
                          />
                          <Route
                            component={PublicWebsiteAdmin}
                            exact
                            path="/maintenance/public-website"
                          />
                          <Route
                            component={MaintenanceAdmin}
                            exact
                            path="/maintenance/maintenance-admin"
                          />
                          <Route
                            component={ElectronicCorrespondenceConfiguration}
                            exact
                            path="/maintenance/electronic-correspondence"
                          />
                          <Route
                            component={RevisionHistorySearch}
                            exact
                            path="/maintenance/revision-history-search"
                          />
                          <Route
                            component={DailyJobsAdmin}
                            exact
                            path="/maintenance/daily-jobs"
                          />
                          <Redirect to="/maintenance/modules" />
                        </Switch>
                      </MaintenanceMain>
                    )}
                  />
                  <AuthorizedRoute path="/events" render={EventsRoutes} />
                  <AuthorizedRoute
                    path="/sketch"
                    render={() => (
                      <Switch>
                        <Route
                          exact
                          path="/sketch/dashboard"
                          render={(props) => (
                            <SketchPageWrapper {...props}>
                              <SketchDashboard {...props} />
                            </SketchPageWrapper>
                          )}
                        />
                        {/* Sketch Dashboard */}
                        <Route
                          exact
                          path="/sketch/sketch-search"
                          render={(props) => (
                            <SketchPageWrapper {...props}>
                              <SketchDashboardSearch {...props} />
                            </SketchPageWrapper>
                          )}
                        />
                        <Route
                          exact
                          path="/sketch/sketch-search/detail/new"
                          render={(props) => (
                            <SketchPageWrapper {...props}>
                              <SketchCreateNew {...props} />
                            </SketchPageWrapper>
                          )}
                        />
                        <Route
                          exact
                          path="/sketch/sketch-search/detail/:sketchId"
                          render={(props) => (
                            <SketchPageWrapper {...props}>
                              <SketchSearchDetail {...props} />
                            </SketchPageWrapper>
                          )}
                        />
                        <Route
                          path="/sketch/draw/:sketchId?"
                          render={(props) => <Sketch {...props} />}
                        />
                        <Redirect to="/sketch/sketch-search" />
                      </Switch>
                    )}
                  />
                  <AuthorizedRoute
                    path="/ci"
                    render={(props) => (
                      <CIPageWrapper {...props}>
                        <Switch>
                          <EditableRoute
                            exact
                            path="/ci/dashboard"
                            component={CIDashboard}
                          />
                          <EditableRoute
                            exact
                            path="/ci/gis-settings"
                            component={GISSettings}
                          />
                          <EditableRoute
                            exact
                            path="/ci/settings"
                            component={CISettings}
                          />
                          <EditableRoute
                            exact
                            path="/ci/symbology/newly-created-property"
                            component={SymbologySettings}
                            key={Date.now()}
                          />
                          <EditableRoute
                            exact
                            path="/ci/symbology/land-value"
                            component={SymbologySettings}
                            key={Date.now()}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-search"
                            component={CIDashboardSearch}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-search/new"
                            component={CINewRecord}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-search/record/:recordId"
                            component={CISearchRecord}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-search/record/:recordId/imagery"
                            component={CISearchImagery}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-county-map"
                            component={CICountyMap}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-appraisal"
                            component={CIAppraisalImagery}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-appraisal/:pid"
                            component={CIAppraisalImagery}
                          />
                          <EditableRoute
                            exact
                            path="/ci/ci-search/record/:recordId/editsketch"
                            component={CISearchSketch}
                          />
                          <Redirect to="/ci/ci-search" />
                        </Switch>
                      </CIPageWrapper>
                    )}
                  />
                  <AuthorizedRoute
                    path="/litigation"
                    render={LitigationRoutes}
                  />
                  <AuthorizedRoute path="/mobile" render={MobileRoutes} />
                  <AuthorizedRoute path="/mra" render={MraRoutes} />
                  <AuthorizedRoute
                    path="/assessment"
                    render={(props) => (
                      <AssessmentPageWrapper {...props}>
                        <Switch>
                          <EditableRoute
                            component={AssessmentExemption}
                            exact
                            path="/assessment/exemption"
                          />

                          <EditableRoute
                            component={AssessmentTaxingUnitMaintenance}
                            exact
                            path="/assessment/taxing-unit"
                          />
                          <EditableRoute
                            component={AssessmentExemptionListing}
                            exact
                            path="/assessment/exemption-listing"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitGeneral}
                            exact
                            path="/assessment/taxmaintenance/general/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitLibrary}
                            exact
                            path="/assessment/taxmaintenance/library/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitVersion}
                            exact
                            path="/assessment/taxmaintenance/version/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitWorksheet}
                            exact
                            path="/assessment/taxmaintenance/worksheet/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitHearingNotice}
                            exact
                            path="/assessment/taxmaintenance/hearing/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitEvent}
                            exact
                            path="/assessment/taxmaintenance/event/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitRevisionHistory}
                            exact
                            path="/assessment/taxmaintenance/revision-history/:taxingUnitID"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitGroups}
                            exact
                            path="/assessment/taxing-unit-groups"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitGroupsCreate}
                            exact
                            path="/assessment/taxing-unit-groups/create"
                          />
                          <EditableRoute
                            component={AssessmentTaxingUnitGroupsGroup}
                            exact
                            path="/assessment/taxing-unit-groups/group/:groupID"
                          />
                          <Redirect to="/assessment/taxing-unit" />
                        </Switch>
                      </AssessmentPageWrapper>
                    )}
                  />
                  <AuthorizedRoute path="/analysis" render={AnalysisRoutes} />
                  <AuthorizedRoute
                    path="/dataCapture"
                    render={DataCaptureRoutes}
                  />
                  <AuthorizedRoute path="/audits" render={AuditsRoutes} />
                  <AuthorizedRoute path="/appeals" render={AppealsRoutes} />
                  <AuthorizedRoute path="/queue" render={QueueRoutes} />
                  <AuthorizedRoute
                    path="/appraisal-process-manager/electronic-delivery-requests-portal-notifications"
                    component={PortalNotificationsPage}
                    exact
                  />
                  <AuthorizedRoute
                    path="/portal-notification-records/:runId"
                    component={PortalNotificationRecords}
                    exact
                  />
                </Switch>
              </AuthorizedWrapper>
            )}
          />
        </Switch>
      </App>
    )}
  />
);
